import { useEffect, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { track } from '../../../utils/analytics'
import type { ConsentFlowStep } from '../../../utils/consentFlowHelper'
import type { ConsentLanguageError } from '../hooks/useConsentLanguage'

export const CONSENT_IMAGE = '/images/consent-background.jpg'
export const OPTIONAL_INTRO_IMAGE = '/images/optional-intro.jpg'
export const RESEARCH_PARTICIPATION_IMAGE = '/images/research-participation.jpg'

export function StepPill({ children }: { children: ReactNode }) {
  return (
    <span className="border-border-base bg-background-secondary inline-flex w-fit rounded-full border px-2 py-1 text-xs font-medium text-[#002C22]">
      {children}
    </span>
  )
}

function ArrowIcon() {
  return <img src="/icons/arrow-right.svg" alt="" className="h-4 w-4" aria-hidden />
}

export function PrimaryAction({
  children,
  disabled,
  onClick,
}: {
  children: ReactNode
  disabled?: boolean
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="disabled:bg-background-disabled disabled:text-text-subtle inline-flex items-center justify-center gap-2 rounded-full bg-[#006045] px-5 py-3 text-sm font-semibold whitespace-nowrap text-white shadow-xs transition hover:bg-[#004d37] focus:ring-2 focus:ring-[#006045]/20 focus:outline-none disabled:cursor-not-allowed"
    >
      <span>{children}</span>
      <ArrowIcon />
    </button>
  )
}

export function ConsentFlowCard({
  children,
  progress,
  imageSrc,
  contentAlign = 'center',
  layout = 'horizontal',
}: {
  children: ReactNode
  progress: ReactNode
  imageSrc: string
  contentAlign?: 'start' | 'center'
  layout?: 'horizontal' | 'vertical'
}) {
  return (
    <div className="flex w-full justify-center font-sans">
      <div className="flex w-full max-w-[900px] flex-col gap-10 pb-10">
        {layout === 'horizontal' ? (
          <section className="border-border-base grid overflow-hidden rounded-xl border bg-white shadow-lg shadow-gray-900/10 md:min-h-[640px] md:grid-cols-[1fr_0.92fr]">
            <div className="bg-background-secondary min-h-64 md:min-h-[640px]">
              <img src={imageSrc} alt="" className="h-full w-full object-cover" aria-hidden />
            </div>
            <div
              className={`flex min-h-[420px] flex-col px-5 py-8 sm:px-8 md:min-h-[640px] ${
                contentAlign === 'start' ? 'justify-start' : 'justify-center'
              }`}
            >
              {children}
            </div>
          </section>
        ) : (
          <section className="border-border-base overflow-hidden rounded-xl border bg-white shadow-lg shadow-gray-900/10">
            <div className="bg-background-secondary h-56 sm:h-72 md:h-80">
              <img src={imageSrc} alt="" className="h-full w-full object-cover" aria-hidden />
            </div>
            <div
              className={`flex flex-col px-5 py-8 sm:px-8 ${
                contentAlign === 'start' ? 'justify-start' : 'justify-center'
              }`}
            >
              {children}
            </div>
          </section>
        )}
        {progress}
      </div>
    </div>
  )
}

function TimelineItems({ items }: { items: ConsentFlowStep[] }) {
  const intl = useIntl()
  return (
    <ol className="space-y-7 border-l border-[#E4E7EC] pl-4">
      {items.map((item) => (
        <li key={`${item.labelKey}-${item.titleKey}`} className="relative">
          <span
            className="absolute top-[42px] -left-[23px] h-3 w-3 rounded-full border border-white bg-[#F2F4F7]"
            aria-hidden
          />
          <StepPill>{intl.formatMessage({ id: item.labelKey })}</StepPill>
          <h3 className="mt-3 text-base font-semibold text-[#002C22]">{intl.formatMessage({ id: item.titleKey })}</h3>
          <p className="text-text-body mt-2 text-sm leading-6">{intl.formatMessage({ id: item.descriptionKey })}</p>
        </li>
      ))}
    </ol>
  )
}

export function WorkflowProgress({ current, next }: { current: ConsentFlowStep[]; next: ConsentFlowStep[] }) {
  return (
    <section className="border-border-base w-full rounded-xl border bg-white px-5 py-6 shadow-sm sm:px-8">
      <h2 className="text-2xl leading-9 font-semibold tracking-tight text-[#002C22]">
        <FormattedMessage id="consentOptions.workflowProgress.currentHeading" defaultMessage="Here's where you are" />
      </h2>
      <div className="mt-6">
        <TimelineItems items={current} />
      </div>
      {next.length > 0 && (
        <>
          <h2 className="mt-8 text-2xl leading-9 font-semibold tracking-tight text-[#002C22]">
            <FormattedMessage id="consentOptions.workflowProgress.nextHeading" defaultMessage="Up Next" />
          </h2>
          <div className="mt-6">
            <TimelineItems items={next} />
          </div>
        </>
      )}
    </section>
  )
}

export function LoadingPanel({ message }: { message: string }) {
  return (
    <div className="space-y-4">
      <StepPill>
        <FormattedMessage id="consentOptions.loadingPanel.step" defaultMessage="Step 1" />
      </StepPill>
      <p className="text-text-body text-sm leading-6" aria-live="polite">
        {message}
      </p>
    </div>
  )
}

/** Avoid consent-to-treat (Step 1) imagery while consent flow is still unknown - used until context loads. */
export function ContextLoadingScreen() {
  return (
    <div className="flex w-full justify-center px-4 py-16 font-sans">
      <p className="text-text-body text-sm leading-6" aria-live="polite">
        <FormattedMessage id="consentOptions.contextLoading.message" defaultMessage="Loading consent details..." />
      </p>
    </div>
  )
}

export function ConsentLanguageErrorPanel({ error }: { error: ConsentLanguageError | null }) {
  const intl = useIntl()
  useEffect(() => {
    track('consent.load_error', { resource: 'language', status: error ?? 'unknown' })
  }, [error])

  let message: string
  if (error === 'multiple_pending_orders') {
    message = intl.formatMessage({
      id: 'consentOptions.consentLanguageError.multiplePendingOrders',
      defaultMessage:
        'We found more than one order awaiting your consent, so we can’t show the right consent form. Please contact us at support@inflectionmedicine.com and we’ll help you complete it.',
    })
  } else if (error === 'consent_not_applicable') {
    message = intl.formatMessage({
      id: 'consentOptions.consentLanguageError.notApplicable',
      defaultMessage:
        'There’s no consent to testing to complete for your account right now. If you think this is a mistake, contact us at support@inflectionmedicine.com.',
    })
  } else {
    message = intl.formatMessage({
      id: 'consentOptions.consentLanguageError.message',
      defaultMessage:
        'Something went wrong loading your consent information. Please refresh the page to try again, or contact us at support@inflectionmedicine.com for help.',
    })
  }

  return <ErrorPanel message={message} />
}

export function ErrorPanel({ message }: { message: string }) {
  return (
    <div className="space-y-4">
      <StepPill>
        <FormattedMessage id="consentOptions.errorPanel.step" defaultMessage="Step 1" />
      </StepPill>
      <h1 className="text-3xl font-semibold tracking-tight text-[#002C22]">
        <FormattedMessage id="consentOptions.errorPanel.heading" defaultMessage="Consent" />
      </h1>
      <p
        className="border-error-500 bg-error-50 text-error-600 rounded-xl border px-4 py-3 text-sm leading-6"
        role="alert"
      >
        {message}
      </p>
    </div>
  )
}
